import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORT_OPENS_HOUR, WHATSAPP_URL, formatSupportHour, isSupportOnline, supportHoursVars } from "@majourneys/shared";
import { Icon } from "@majourneys/icons";
import { cn } from "../lib/utils";
import { BreathingDot } from "./breathing-dot";
import { PW, pewterButton, pewterPanelApproved } from "./pewter-shell";
import { useOptionalBookingContext } from "../provider";

// Support hours live in @majourneys/shared (one value for every surface);
// re-exported here so existing imports keep working.
export { SUPPORT_OPENS_HOUR, SUPPORT_CLOSES_HOUR, isSupportOnline } from "@majourneys/shared";

/**
 * Online or away, read only after mount and refreshed every minute. Null on
 * the server and on the first client render: the server runs in UTC and the
 * phone anywhere, so a status computed during SSR would disagree with the
 * hydrating client and throw React #418.
 */
function useSupportOnline(fixed?: Date): boolean | null {
  const [online, setOnline] = useState<boolean | null>(null);
  useEffect(() => {
    if (fixed) return;
    const tick = () => setOnline(isSupportOnline(new Date()));
    tick();
    const id = window.setInterval(tick, 60_000);
    return () => window.clearInterval(id);
  }, [fixed]);
  return fixed ? isSupportOnline(fixed) : online;
}

interface NeedHelpCardProps {
  className?: string;
  /** Pins the clock (tests). */
  now?: Date;
  /** The place on the page ("Dar Soukkar"), so a question to the AI is about it. */
  subject?: string;
}

/** The question as the AI receives it: about this place, even when the guest did not name it. */
export function aiQuestionAbout(question: string, subject?: string): string {
  const q = question.trim();
  if (!subject || q.toLowerCase().includes(subject.toLowerCase())) return q;
  return `${q} (${subject})`;
}

/**
 * "#08 Need help" from the pewter lab (owner pick): the pewter panel, a navy
 * coin with the gold chat mark, whether someone is there right now, and one
 * real button to WhatsApp instead of a phone number in plain text.
 */
export function NeedHelpCard({ className, now, subject }: NeedHelpCardProps) {
  const { t, i18n } = useTranslation();
  const online = useSupportOnline(now);
  // Outside support hours, MaJourneys AI answers now (round 18, item 144):
  // the card used to say "Back at 10:00" exactly when people plan tonight.
  const aiAskHref = useOptionalBookingContext()?.aiAskHref;
  const askAi = online === false && !!aiAskHref;
  const [question, setQuestion] = useState("");
  const ask = (e: FormEvent) => {
    e.preventDefault();
    if (!aiAskHref || !question.trim()) return;
    window.location.assign(aiAskHref(aiQuestionAbout(question, subject), i18n.language));
  };

  return (
    <section
      aria-labelledby="need-help-title"
      data-testid="need-help-card"
      className={cn("p-3.5", className)}
      style={pewterPanelApproved(20)}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full"
          style={{ background: PW.navyGradient, color: PW.gold }}
        >
          <Icon name="ri-whatsapp-line" className="text-[19px]" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 id="need-help-title" className="text-balance font-sans text-[15px] font-semibold leading-snug" style={{ color: PW.navy }}>
            {t("activity.help_title")}
          </h3>
          <p
            data-testid="need-help-status"
            data-state={online === null ? "unknown" : online ? "online" : "away"}
            className="mt-0.5 flex items-center gap-1.5 font-sans text-[12.5px] leading-snug"
            style={{ color: PW.muted }}
          >
            {online === null ? (
              /* Neutral on SSR and first paint: the hours, which never change. */
              t("activity.support_hours", supportHoursVars(i18n.language))
            ) : online ? (
              <>
                <BreathingDot color="#1FA855" />
                {t("activity.help_online")}
              </>
            ) : askAi ? (
              <>
                <BreathingDot color="#1FA855" />
                {t("activity.help_away_ai", {
                  time: formatSupportHour(SUPPORT_OPENS_HOUR, i18n.language),
                  defaultValue: "MaJourneys AI answers now. Our team is back at {{time}}.",
                })}
              </>
            ) : (
              <>
                <BreathingDot color={PW.gold} still />
                {t("activity.help_away", { time: formatSupportHour(SUPPORT_OPENS_HOUR, i18n.language) })}
              </>
            )}
          </p>
        </div>
      </div>
      {askAi ? (
        <>
          <form
            onSubmit={ask}
            className="mt-3 flex min-h-[44px] items-center gap-2 rounded-full py-1 pe-1 ps-4"
            style={{ background: PW.controlFill, boxShadow: `0 0 0 1px ${PW.controlEdge}` }}
            data-testid="need-help-ask"
          >
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              aria-label={t("activity.help_ask_label", { defaultValue: "Your question for MaJourneys AI" })}
              placeholder={
                subject
                  ? t("activity.help_ask_placeholder", { name: subject, defaultValue: "Ask about {{name}}" })
                  : t("activity.help_ask_placeholder_plain", { defaultValue: "Ask MaJourneys AI" })
              }
              className="min-w-0 flex-1 bg-transparent font-sans text-[13.5px] font-medium outline-none placeholder:text-[#5a5d77]"
              style={{ color: PW.navy }}
            />
            <button
              type="submit"
              aria-label={t("activity.help_ask_send", { defaultValue: "Ask MaJourneys AI" })}
              className="mj-press grid h-9 w-9 shrink-0 place-items-center rounded-full text-white"
              style={{ background: PW.navyGradient }}
            >
              <Icon name="ri-send-plane-2-line" className="text-[15px]" />
            </button>
          </form>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2.5 flex min-h-[24px] items-center gap-1.5 font-sans text-[12.5px] font-semibold underline"
            style={{ color: PW.navy }}
          >
            <Icon name="ri-whatsapp-line" className="text-[14px]" aria-hidden />
            {t("activity.help_whatsapp_later", {
              time: formatSupportHour(SUPPORT_OPENS_HOUR, i18n.language),
              defaultValue: "Or leave us a WhatsApp, we reply from {{time}}",
            })}
          </a>
        </>
      ) : (
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-full font-sans text-[13.5px] font-semibold transition-transform duration-150 active:scale-[0.98]"
        style={{ ...pewterButton, color: PW.navy }}
      >
        <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full" style={{ background: "#25D366" }} />
        {t("activity.help_whatsapp")}
      </a>
      )}
    </section>
  );
}
